import { useRef, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";

import { toCount } from "common/utils/formatting";
import { toggleValue } from "common/utils/sets";
import { PaymentMethod } from "common/types";
import CatalogImage from "common/components/CatalogImage";
import { deepCombineErrors } from "common/utils/errors";
import { titleCased } from "common/utils/strings";
import { scrollOverflowAncestorToTop } from "common/utils/dom";

import { BasicSubscription } from "client/subscriptions/types";
import {
  usePaginatedBasicSubscriptionsQuery,
  useUpdateBulkSubscriptionsMutation,
} from "client/subscriptions/hooks";
import { NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES } from "client/subscriptions/constants";
import Checkbox from "client/common/components/inputs/Checkbox";
import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import Pagination from "client/common/components/layouts/Pagination";

import styles from "../../styles/SubscriptionBulkUpdatePaymentMethod.module.less";

export type SubscriptionBulkUpdatePaymentMethodDrawerProps = {
  paymentMethod: PaymentMethod;
  onSuccess: () => void;
};

const SubscriptionBulkUpdatePaymentMethodDrawer = ({
  paymentMethod,
  onSuccess,
}: SubscriptionBulkUpdatePaymentMethodDrawerProps) => {
  const { close } = useCurrentDrawer();
  const stripe = useStripe();
  const listRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [excludedSubscriptionIds, setExcludedSubscriptionIds] = useState<Set<number>>(new Set());

  const handleClose = () => {
    onSuccess();
    close();
  };

  const {
    mutate: bulkUpdate,
    isPending: isLoading,
    error,
  } = useUpdateBulkSubscriptionsMutation({
    onSuccess: handleClose,
    onError: (e, data) => {
      if (!stripe || !e.data?.stripe_action_client_secret) {
        return;
      }
      stripe?.handleCardAction(e.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          bulkUpdate(data);
        }
      });
    },
  });

  const {
    data,
    isLoading: isSubscriptionsLoading,
    error: subscriptionsError,
  } = usePaginatedBasicSubscriptionsQuery({
    visible_status: NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES,
    exclude_payment_method_id: paymentMethod.id,
    page,
  });

  const subscriptions = data?.results ?? [];
  const totalCount = data?.paginator.count ?? 0;
  const selectedCount = totalCount - excludedSubscriptionIds.size;
  const canSubmit = selectedCount > 0;

  const onSubmit = () => {
    bulkUpdate({
      payment_method_id: paymentMethod.id,
      visible_status: NON_CANCELED_NON_IMPORTED_SUBSCRIPTION_STATUSES,
      exclude_payment_method_id: paymentMethod.id,
      ...(excludedSubscriptionIds.size > 0
        ? { exclude_subscription_ids: [...excludedSubscriptionIds] }
        : {}),
    });
  };

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    scrollOverflowAncestorToTop(listRef.current);
  };

  if (isSubscriptionsLoading && !data) {
    return <DrawerLayout.Loading />;
  }

  if (subscriptionsError || !data) {
    return (
      <DrawerLayout.FetchError entityName="autoship" isPlural={true} error={subscriptionsError} />
    );
  }

  const modalLayoutHeader = (
    <div className={styles.nameContainer}>
      You have Autoships with another payment method on file. Select the Autoships you would like to
      update to the new payment method.
    </div>
  );

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update your Autoships"
        description={modalLayoutHeader}
        onClose={close}
      />
      <DrawerLayout.Form
        canSubmit={canSubmit}
        onSubmit={onSubmit}
        submitText={`Update ${toCount(selectedCount, "Autoship")}`}
        isLoading={isLoading}
        error={error}
        fieldErrors={[deepCombineErrors(error?.errors)]}
      >
        <div ref={listRef} className={styles.list}>
          {subscriptions.map((subscription: BasicSubscription) => (
            <DrawerLayout.Form.Group key={subscription.id}>
              <div className={styles.option}>
                <Checkbox
                  value={!excludedSubscriptionIds.has(subscription.id)}
                  onChange={() =>
                    setExcludedSubscriptionIds(
                      toggleValue(excludedSubscriptionIds, subscription.id),
                    )
                  }
                />
                <div className={styles.item}>
                  <CatalogImage
                    image={subscription.item.image}
                    size="thumbnail"
                    alt={subscription.item.full_name}
                    className={styles.image}
                  />
                  <div className={styles.nameContainer}>
                    <div className={styles.name}>
                      {subscription.item.product_line_name} {subscription.item.name} for{" "}
                      <b>{subscription.patient.name_display}</b>
                    </div>
                    {paymentMethod && subscription.payment_method && (
                      <div className={styles.name}>
                        Currently charged to:
                        <div className={styles.name}>
                          <b>
                            {subscription.payment_method.is_klarna
                              ? subscription.payment_method.alt_ui_string
                              : `${titleCased(subscription.payment_method.brand) || "Card"}${
                                  subscription.payment_method.last_four
                                    ? ` *${subscription.payment_method.last_four}`
                                    : ""
                                }${
                                  subscription.payment_method.expiration_month &&
                                  subscription.payment_method.expiration_year
                                    ? `, exp. ${subscription.payment_method.expiration_month}/${subscription.payment_method.expiration_year}`
                                    : ""
                                }`}
                          </b>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </DrawerLayout.Form.Group>
          ))}
          <Pagination
            paginator={data.paginator}
            onPageChange={handlePageChange}
            entityName="Autoship"
          />
        </div>
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

export default SubscriptionBulkUpdatePaymentMethodDrawer;
